import { lazyAssets, LazyAsset } from '@sdk/lazy';
import { useAgentContext } from '@src/components/agent-layout/agent-layout';
import { canonicalPath, isOpenableProjectPath, selectProjectContext } from '@src/components/project-selector';
import { projectScope } from '@src/lib/scope-filter';
import { useDockNavigation, useIsHomeSurface } from '@src/navigation/useDockNavigation';
import { DockPointer } from '@src/navigation/DockPointer';
import { dockForProjectEntry } from '@src/tabs/project-entry';
import { newProjectLocale } from '@src/contexts/locale-context';
import { notify } from '@src/notifications';
import { dataContext, isHubOnly, PageId, Project } from '@sdk';
import { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';
import { withHomePage } from '@src/project-home-page/home-page-state';
import { ViewType } from '@src/types/ViewType';

// ---------------------------------------------------------------------------
// Shared path helpers — one source of truth for the project-open flow so the
// dialog (`OpenProjectComponent`) and any direct-open affordance (e.g. the Vibe
// home "Open project folder" button) can't drift apart. Dedup/compare keys go
// through `canonicalPath` (from project-selector); `normalizePath` keeps a
// leading slash for display.
// ---------------------------------------------------------------------------

export const normalizePath = (path: string): string => {
  const normalized = path.trim().replace(/\\/g, '/');
  if (!normalized) return '';
  if (normalized === '/') return '/';
  return normalized.replace(/\/+$/, '');
};

// ---------------------------------------------------------------------------
// useProjectOpener — the shared "open/switch to a project" flow
// ---------------------------------------------------------------------------

export interface UseProjectOpenerOptions {
  /** Called after a project is picked, before context is adopted. */
  onProjectChanged?: () => void;
  /** Gate/map continuation: when set, context is adopted here and the caller's
   *  own navigation/stamping runs in this callback (see OpenProjectComponent). */
  onPicked?: (project: Project) => void | Promise<void>;
  /** Surface a user-facing error (e.g. folder-picker failure). */
  onError?: (message: string) => void;
}

export function useProjectOpener({ onProjectChanged, onPicked, onError }: UseProjectOpenerOptions = {}) {
  const { t } = useLingui();
  const { computeNode } = useAgentContext();
  const { currentDock, navigation } = useDockNavigation();
  // Surface-derived, not a prop: on ANY home surface (root `/` or /dock/home,
  // in any view mode) switching a project just changes the project and lands
  // on the fresh home; only from within a workspace/dock does it resume the
  // target's build process (Vibe) or last-active tab (Standard/Advanced).
  // Keeps the hero buttons, the modal, AND the footer Switch Project consistent.
  const isHome = useIsHomeSurface();

  const setCurrentProjectContext = useCallback(
    async (project: Project) => {
      onProjectChanged?.();
      if (onPicked) {
        // Gate/map flows don't navigate through a project loader, so adopt the
        // context here. Entity stamping (task/conversation/project_id, mapping
        // table writes, remap navigation) happens inside `onPicked` — the
        // gate's apply callback owns it (and its own navigation) so the
        // wasReplacement signal isn't clobbered by a pre-stamp here.
        await selectProjectContext(project);
        try {
          await onPicked(project);
        } catch {
          // continuation errors shouldn't break the picker
        }
      } else {
        // Hub: there's no desk home / vibe hero / tab strip to land on, and no
        // build process to resume — the hub's own project card goes straight to
        // the project dock on the hub page, so every hub open lands the same way.
        if (isHubOnly()) {
          await selectProjectContext(project);
          navigation.openDock(DockPointer.forProject(project.id).withPage(PageId.HUB));
          return;
        }
        // Opening a project just opens the project — whatever surface is on screen.
        // (Vibe used to resume the project's build process or land on a
        // no-process Vibe pane; everything is a tab now, so the project's last tab
        // — or its landing — is the answer for every surface.)
        if (isHome) {
          // URL-first: the scope-carrying HOME dock's loader
          // (adoptScopeProject) is the single writer of project context.
          navigation.openDock(withHomePage(DockPointer.forHome().withScopeFilter(projectScope(project.id))));
          return;
        }
        // Plain switch (footer Switch Project included): navigate to the
        // project's tab — the same path as clicking that tab in the strip
        // (dockForProjectEntry → fromTabHash → openDock). Resumes the KNOWN
        // last-active tab; with no known tab, a scope-keyed current view
        // (Assets/Explorer/Desktop) re-scopes to the destination, else the
        // project landing. No context pre-write here: the destination dock's
        // loader is the single writer of project context (URL-first).
        // A switch that lands on the project's page or the home asks for the
        // project's home page. A resumed tab is left as it is: its loader runs
        // no load redirect, and the option would only linger in its URL.
        const entry = await dockForProjectEntry(project.id, currentDock);
        const landing = entry.viewType === ViewType.PROJECT || entry.viewType === ViewType.HOME;
        // A resumed tab is a TOP-LEVEL tab, the same as its chip in the strip:
        // never a child of the workspace being left, never painted in its mode.
        navigation.openDock(landing ? withHomePage(entry) : entry, undefined, { topLevel: true });
      }
    },
    [isHome, onProjectChanged, onPicked, navigation, currentDock],
  );

  const ensureProjectAndSetContext = useCallback(
    async (path: string) => {
      if (!dataContext.someone) throw new Error(t`You must be logged in`);

      const normalizedPath = normalizePath(path);
      if (!normalizedPath || !isOpenableProjectPath(normalizedPath)) {
        throw new Error(t`Please provide a valid project path`);
      }

      const pathKey = canonicalPath(normalizedPath);
      const freshProjects = await lazyAssets.refresh(LazyAsset.Projects);
      // Dedup on the mount path alone — a name is not a location.
      let targetProject =
        freshProjects.find((p) => canonicalPath(p.fs_storage_mount_path || '') === pathKey) || null;
      const openedExisting = !!targetProject;

      if (!targetProject) {
        targetProject = new Project({ name: normalizedPath, locale: newProjectLocale() });
        targetProject = await targetProject.save([dataContext.someone]);
      }

      await targetProject.setupForDesktop();
      await setCurrentProjectContext(targetProject);
      return { project: targetProject, openedExisting };
    },
    [setCurrentProjectContext, t],
  );

  const pickFolder = useCallback(
    async (initialDir?: string): Promise<string | null> => {
      if (!computeNode) {
        onError?.(t`No compute node available`);
        return null;
      }
      try {
        return await computeNode.openPathDialog(initialDir);
      } catch {
        onError?.(t`Failed to open folder picker`);
        return null;
      }
    },
    [computeNode, onError, t],
  );

  /** Pick a folder from disk and open (or create) the project rooted there.
   *  Never throws — cancel returns `{ opened: false }`, failures route through
   *  `onError`, so callers only manage their own busy state. */
  const openProjectFolder = useCallback(
    async (initialDir?: string): Promise<{ opened: boolean }> => {
      const selected = await pickFolder(initialDir);
      if (!selected) return { opened: false };
      try {
        const { openedExisting, project } = await ensureProjectAndSetContext(selected);
        if (openedExisting) {
          notify.success({ title: t`Opened existing project`, message: project.displayName });
        }
        return { opened: true };
      } catch (err) {
        onError?.(err instanceof Error ? err.message : t`Failed to open project`);
        return { opened: false };
      }
    },
    [pickFolder, ensureProjectAndSetContext, onError, t],
  );

  return {
    computeNode,
    ensureProjectAndSetContext,
    pickFolder,
    openProjectFolder,
    // The landing policy above (see `setCurrentProjectContext`), for callers
    // holding an already-minted Project rather than a path — e.g. a git clone,
    // where the backend minted it. A `ProjectLanding`.
    openExistingProject: setCurrentProjectContext,
  };
}
